import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FormField } from '../components/FormField';
import { Button } from '../components/Button';
import { TechLayout } from '../components/TechLayout';
import { Toast } from '../components/Toast';
import { AlertCircleIcon, HelpCircleIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { getDraftsByEmail } from '../services/draftService';
import { trackEvent, EVENTS } from '../services/analyticsService';
export function Login() {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [toast, setToast] = useState({
    isVisible: false,
    type: 'success' as 'success' | 'error' | 'info' | 'warning',
    message: ''
  });
  const {
    login
  } = useAuth();
  const navigate = useNavigate();
  // Get redirect path and draft ID from location state
  const redirectPath = location.state?.from || '/';
  const draftId = location.state?.draftId || null;
  // Check if there's a verified param in the URL (from email verification)
  const searchParams = new URLSearchParams(location.search);
  const verified = searchParams.get('verified') === 'true';
  useEffect(() => {
    // Show success toast if user was just verified
    if (verified) {
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Email verified successfully! You can now log in.'
      });
      // Track verification event
      trackEvent(EVENTS.EMAIL_VERIFIED, {
        source: 'email_link'
      });
    }
  }, [verified]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const user = await login(email, password, rememberMe);
      // Track successful login
      trackEvent(EVENTS.LOGIN_SUCCESS, {
        userType: user.type,
        hasDraft: !!draftId
      });
      // If there's a draft ID, we'll navigate to the appropriate form
      if (draftId) {
        // Navigate based on user type with the draft ID
        if (user.type === 'brand') {
          navigate('/brand', {
            state: {
              draftId
            }
          });
        } else if (user.type === 'organizer') {
          navigate('/organizer', {
            state: {
              draftId
            }
          });
        } else if (user.type === 'community') {
          navigate('/community/register', {
            state: {
              draftId
            }
          });
        } else {
          navigate('/dashboard/brand');
        }
      } else {
        // Check if user has any drafts by email
        const drafts = await getDraftsByEmail(email);
        if (drafts.length > 0) {
          // Show toast about found drafts
          setToast({
            isVisible: true,
            type: 'info',
            message: 'We found draft profiles associated with your email. You can continue them from your dashboard.'
          });
        }
        // Redirect based on user type
        if (user.type === 'brand') {
          navigate('/dashboard/brand');
        } else if (user.type === 'organizer') {
          navigate('/dashboard/organizer');
        } else if (user.type === 'community') {
          navigate('/community');
        } else if (user.type === 'admin') {
          navigate('/admin');
        } else {
          navigate(redirectPath);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Failed to log in. Please check your credentials.');
      // Track login failure
      trackEvent(EVENTS.LOGIN_FAILURE, {
        reason: 'invalid_credentials'
      });
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-2394">
      <div className="sm:mx-auto sm:w-full sm:max-w-md" data-id="element-2395">
        <h2 className="text-center text-3xl font-extrabold text-gray-900" data-id="element-2396">
          Log in to your account
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md" data-id="element-2397">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10" data-id="element-2398">
          <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2399">Log In</h1>
          {error && <div className="bg-red-50 text-red-700 p-3 rounded-md mb-4 flex items-start" data-id="element-2400">
              <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-2401" />
              <div data-id="element-2402">
                <p data-id="element-2403">{error}</p>
                <Link to="/login/help" className="text-red-700 underline text-sm font-medium mt-1 inline-block" data-id="element-2404">
                  Having trouble logging in?
                </Link>
              </div>
            </div>}
          <form onSubmit={handleSubmit} data-id="element-2405">
            <div className="space-y-4" data-id="element-2406">
              <FormField label="Email Address" id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} data-id="element-2407" />
              <div className="space-y-1" data-id="element-2408">
                <label htmlFor="password" className="block text-sm font-medium text-gray-800" data-id="element-2409">
                  Password <span className="text-red-500" data-id="element-2410">*</span>
                </label>
                <div className="relative" data-id="element-2411">
                  <input id="password" type={showPassword ? 'text' : 'password'} className="block w-full rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50" value={password} onChange={e => setPassword(e.target.value)} required data-id="element-2412" />
                  <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)} data-id="element-2413">
                    {showPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400" data-id="element-2414" /> : <EyeIcon className="h-5 w-5 text-gray-400" data-id="element-2415" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between" data-id="element-2416">
                <div className="flex items-center" data-id="element-2417">
                  <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} data-id="element-2418" />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700" data-id="element-2419">
                    Remember me
                  </label>
                </div>
                <div className="text-sm" data-id="element-2420">
                  <Link to="/login/help" className="font-medium text-blue-600 hover:text-blue-500" data-id="element-2421">
                    Forgot your password?
                  </Link>
                </div>
              </div>
              <Button type="submit" variant="primary" className="w-full" disabled={isLoading} techEffect={true} data-id="element-2422">
                {isLoading ? 'Logging in...' : 'Log In'}
              </Button>
            </div>
          </form>
          <div className="mt-6" data-id="element-2423">
            <div className="relative" data-id="element-2424">
              <div className="absolute inset-0 flex items-center" data-id="element-2425">
                <div className="w-full border-t border-gray-300" data-id="element-2426" />
              </div>
              <div className="relative flex justify-center text-sm" data-id="element-2427">
                <span className="px-2 bg-white text-gray-500" data-id="element-2428">Or</span>
              </div>
            </div>

            <div className="mt-6" data-id="element-2429">
              <button onClick={() => navigate('/demo')} className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-2430">
                Access Demo Accounts
              </button>
            </div>
          </div>
        </div>
      </div>
      <Toast type={toast.type} message={toast.message} duration={5000} onClose={() => setToast({
      ...toast,
      isVisible: false
    })} isVisible={toast.isVisible} data-id="element-2431" />
      <style jsx data-id="element-2432">{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .animate-spin-slow {
          animation: spin-slow linear infinite;
        }
      `}</style>
    </div>;
}